import React, { useState, useEffect, useCallback } from "react";
import Header from "./components/Header";
import TabNavigation from "./components/TabNavigation";
import VideoTab from "./components/VideoTab";
import ImageTab from "./components/ImageTab";
import DetectionResults from "./components/DetectionResults";
import Footer from "./components/Footer";
import NotificationContainer from "./components/NotificationContainer";
import { apiService } from "./services/apiService";

const App = () => {
  const [activeTab, setActiveTab] = useState("video");
  const [notifications, setNotifications] = useState([]);
  const [detections, setDetections] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);

  // Add notification
  const addNotification = useCallback((message, type = "info") => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
  }, []);

  // Remove notification
  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Check API health on mount
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const health = await apiService.checkHealth();
        if (health.status === "healthy") {
          console.log("✅ API connected");
          if (!health.model_loaded) {
            addNotification(
              "⚠️ YOLO model not loaded - some features may not work",
              "warning"
            );
          }
        } else {
          addNotification("API server is not healthy", "warning");
        }
      } catch (error) {
        addNotification("Cannot connect to API server on port 8000", "error");
      }
    };

    checkHealth();
  }, [addNotification]);

  // Auto-refresh data when detecting
  useEffect(() => {
    let interval;
    if (isDetecting) {
      interval = setInterval(async () => {
        try {
          const [statusData, resultsData, summaryData] = await Promise.all([
            apiService.getDetectionStatus(),
            apiService.getDetectionResults(8),
            apiService.getDetectionSummary(),
          ]);

          setStatus(statusData);
          setDetections(resultsData.results || []);
          setSummary(summaryData);

          if (!statusData.is_active) {
            setIsDetecting(false);
          }
        } catch (error) {
          console.error("Auto-refresh error:", error);
        }
      }, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isDetecting]);

  const appProps = {
    addNotification,
    setIsDetecting,
    setStatus,
    setDetections,
    setSummary,
    isDetecting,
    status,
    detections,
    summary,
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
      <Header />

      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === "video" ? (
        <VideoTab {...appProps} />
      ) : (
        <ImageTab {...appProps} />
      )}

      {activeTab === "video" && (
        <DetectionResults
          detections={detections}
          summary={summary}
          isDetecting={isDetecting}
        />
      )}

      <Footer />

      <NotificationContainer
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default App;
